import { useNavigate } from "react-router-dom"

import { Button, Flex } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { CheckCircleOutlined, SaveOutlined } from "@/icons"
import { CancelConfirmModal, useConfirmModal } from "@/molecules/ConfirmModal"
import { Form } from "@/organisms/Form/Form"

import type { FooterComponent } from "."

export const Footer: FooterComponent = (props) => {
  const { t } = useTranslation()
  const { confirm } = useConfirmModal()
  const navigate = useNavigate()
  const { custom, formId, isNewEntity, permissions, form } = Form.useForm()
  const { isDirty } = custom || {}
  const id = `footer-${formId}`
  const { createKey = "general.save", updateKey = "general.save" } = props
  const tKey = isNewEntity ? createKey : updateKey
  const { disabled, okText, saveGuard, cancelRedirect, preventSubmit } = props
  const isCancelRedirect = cancelRedirect !== undefined
  const { OkIcon = isCancelRedirect ? SaveOutlined : CheckCircleOutlined } =
    props

  return (
    <Flex id={id} gap="small" justify="end">
      <Button
        id="cancel-button-footer-form"
        onClick={() => {
          const onOk = () => {
            custom.setIsDirty(false)

            if (isCancelRedirect)
              window.queueMicrotask(() => {
                navigate(cancelRedirect)
              })
            else form.resetFields()
          }
          let action = isNewEntity ? "create" : "update"

          if (!isCancelRedirect) action = "reset"

          if (isDirty)
            return confirm((attr) =>
              CancelConfirmModal({
                onOk,
                okText: t(`forms.buttons.cancel.${action}`),
                cancelText: !isCancelRedirect
                  ? t("general.backToEdit")
                  : undefined,
                content: t(`forms.messages.cancel.${action}`),
                ...attr
              })
            )

          onOk()
        }}
      >
        {t("general.cancel")}
      </Button>
      {props.children}
      <Button
        id="submit-button-footer-form"
        disabled={disabled || !isDirty || !permissions.write}
        type="primary"
        icon={<OkIcon />}
        loading={props.submitting}
        onClick={async () => {
          try {
            await form.validateFields()

            const values = form.getFieldsValue() || {}

            if (preventSubmit?.(values, form)) return

            if (props.saveGuard) {
              const saveGuardProps =
                typeof saveGuard === "function"
                  ? saveGuard(isNewEntity, values)
                  : saveGuard

              confirm((attr) =>
                CancelConfirmModal({
                  okButtonProps: {
                    "data-testid": "ok-button-save-guard-modal"
                  },
                  cancelText: t("general.cancel"),
                  cancelButtonProps: {
                    "data-testid": "cancel-button-save-guard-modal"
                  },
                  okText: t("general.save"),
                  onOk: form.submit,
                  ...saveGuardProps,
                  ...attr
                })
              )
            } else form.submit()
          } catch {
            return
          }
        }}
      >
        {okText?.(isNewEntity) || t(tKey)}
      </Button>
    </Flex>
  )
}
